import React from 'react';
import _ from 'lodash';
import type { NextPage } from 'next';
import Head from 'next/head';
import log from '@wix/wix-log';
import { Box, WixDesignSystemProvider } from '@wix/design-system';
import { Platform } from '@wix/ambassador-wixstudio-templates-v1-template/types';

import TemplateViewer from '@components/TemplateViewer';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import { viewListingPath } from '@components/Hooks/useRouter';
import {
  WixNextApiRequest,
  WixNextApiResponse,
  ViewTemplateData,
  Template as TemplateData,
} from '@common/types';
import TemplatePageHeader from '@components/Template/TemplatePageHeader';
import NotFoundErrorState from '@components/Template/ErrorStates/NotFoundErrorState';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { CurrencyContextProvider } from '@components/Contexts/CurrencyContext';
import { getWixContext } from '@utils/async-store';
import createInitialStateService from '@services/InitialStateService';
import { getPageLanguage } from '@app/utils';
import { TemplateContextProvider } from '@components/Contexts/TemplateContext';

const TemplateViewerPage: NextPage<{
  isMobileUserAgent: boolean;
  viewTemplateData: ViewTemplateData;
  entry: string;
  language: string;
  error?: string;
  host: string;
  origin?: string;
  isListing: boolean;
  userCurrency: string;
  currencyConversionRate: number;
  taxRate: number;
}> = ({
  isMobileUserAgent,
  viewTemplateData,
  error,
  language,
  entry,
  host,
  isListing,
  userCurrency,
  currencyConversionRate,
  taxRate,
  origin,
}) => {
  const TemplatePreviewHeader = () => {
    const brand =
      viewTemplateData?.editorType === Platform.STUDIO
        ? 'Wix Studio'
        : 'Wix.com';
    const title = _.isUndefined(viewTemplateData?.title)
      ? `${viewTemplateData?.templateName} | ${brand}`
      : `${viewTemplateData?.title} | Wix Marketplace | ${brand}`;
    return (
      <Head>
        <title>{title}</title>
        <meta name="robots" content="noindex, nofollow" />
        {isMobileUserAgent && viewTemplateData?.editorType === Platform.WIX && (
          <meta name="viewport" content="width=320, user-scalable=yes" />
        )}
      </Head>
    );
  };

  return (
    <>
      <TemplatePreviewHeader />
      <BiSessionContextProvider>
        <WixDesignSystemProvider features={{ newColorsBranding: true }}>
          <CurrencyContextProvider
            currencyData={{
              currencyCode: userCurrency,
              conversionRate: currencyConversionRate,
            }}
            taxRate={taxRate}
          >
            <HeaderContextProvider host={host} entry={entry} origin={origin}>
              {error ? (
                <>
                  <TemplatePageHeader />
                  <Box
                    height={`calc(100vh - ${
                      isMobileUserAgent ? '54px' : '72px'
                    })`}
                  >
                    <NotFoundErrorState />
                  </Box>
                </>
              ) : (
                <TemplateContextProvider
                  language={language}
                  template={
                    {
                      id: viewTemplateData.templateId!,
                      accountId: viewTemplateData.ownerAccountId,
                      platform: Platform.STUDIO,
                      language,
                    } as TemplateData
                  }
                >
                  <TemplateViewer
                    isListing={isListing}
                    entry={entry}
                    isMobileUserAgent={isMobileUserAgent}
                    viewTemplateData={viewTemplateData}
                    language={language}
                  />
                </TemplateContextProvider>
              )}
            </HeaderContextProvider>
          </CurrencyContextProvider>
        </WixDesignSystemProvider>
      </BiSessionContextProvider>
    </>
  );
};

export async function getServerSideProps({
  req,
  res,
  params,
  query,
}: {
  req: WixNextApiRequest;
  res: WixNextApiResponse;
  params: any;
  query: any;
}) {
  const {
    sourceAndId: [source, id],
  } = params;
  const { entry, origin } = query;
  const wixContext = getWixContext();
  const initialStateService = createInitialStateService(req.aspects, res);

  const templateListingAPI = wixContext.serviceFactory.templateListingAPI();
  const templatesAPI = wixContext.serviceFactory.templatesAPI();
  const businessNameAPI = wixContext.serviceFactory.businessNameAPI();
  const metaSiteReadAPI = wixContext.serviceFactory.metaSiteReadAPI();
  const currencyAPI = wixContext.serviceFactory.currencyAPI();

  const language = getPageLanguage({ aspects: req.aspects });

  try {
    const isListing = source === viewListingPath;

    let templateListing;
    if (isListing) {
      templateListing = await templateListingAPI.getTemplateById(id);
    }
    const publicTemplate = await templatesAPI.getPublicTemplateById(
      isListing ? templateListing.templateId : id,
    );

    if (!publicTemplate) {
      throw Error('publicTemplate not found');
    }
    const { template, templateOwnerAccountId } = publicTemplate;

    const publicMetaSiteId = template?.publicOptions?.publicMetaSiteId;
    const editorType = template?.platform;
    const [templateUrl, businessName] = await Promise.all([
      metaSiteReadAPI.getSiteURL(publicMetaSiteId!),
      businessNameAPI.getBusinessName(templateOwnerAccountId),
    ]);
    const viewTemplateData = {
      businessName,
      templateId: templateListing?.id || template?.id,
      price: templateListing?.price,
      title: templateListing?.title,
      templateName: template?.name,
      templateUrl: templateUrl?.url,
      ownerAccountId: templateOwnerAccountId,
      hasVeloOrBlocks: template?.publicOptions?.includesCode,
      editorType,
      privacyPolicyUrl: templateListing?.privacyPolicyUrl || '',
    };

    const { userCurrency, currencyConversionRate, countryTaxRate } =
      await currencyAPI.getGeoBasedTaxAndCurrencyRate({
        aspects: req.aspects,
        loggerName: 'viewTemplate',
      });

    const props = {
      viewTemplateData: JSON.parse(JSON.stringify(viewTemplateData)),
      language,
      isListing,
      ...(entry && { entry }),
      ...(origin && { origin }),
      userCurrency,
      currencyConversionRate,
      taxRate: countryTaxRate,
    };
    const initialState = await initialStateService.create();
    return {
      props: {
        initialState,
        ...props,
      },
    };
  } catch (error: any) {
    log('viewTemplate')
      .withAspects(req.aspects)
      .error(`Error: ${error.message}`);
    const initialState = await initialStateService.create();
    return {
      props: {
        initialState,
        partnerProfile: {},
        error: error.message,
      },
    };
  }
}

export default TemplateViewerPage;
